import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3152';

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

/** Réponse GET /U3152/initLU + logicalUnitCode fusionné côté shell. */
export interface U3152InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
}

/** Ligne POST /U3152/search. */
export interface U3152ContactTypeSearchRow {
  id: string;
  contactTypeCode?: string;
  active?: boolean;
  contactTypeNameFr?: string;
  contactTypeNameEn?: string;
  version: number;
  deletedAt?: string | Date | null;
  createdAt?: string | Date;
}

export interface U3152ContactTypeTranslation {
  id?: string;
  languageCode: string;
  contactTypeName: string;
  version?: number;
}

/** Détail GET /U3152/:id (result). */
export interface U3152ContactType {
  id: string;
  contactTypeCode: string;
  active: boolean;
  translations: U3152ContactTypeTranslation[];
  version: number;
}

export type U3152ContactTypeTranslationFormRow = U3152ContactTypeTranslation;

export type U3152ContactTypeFormRow = U3152ContactType;

export interface U3152ContactTypeCreateBody {
  contactTypeCode: string;
  active: boolean;
  translations: Array<{
    languageCode: string;
    contactTypeName: string;
  }>;
}

export interface U3152ContactTypeUpdateBody {
  id: string;
  active: boolean;
  version: number;
  translations: Array<
    | {
        languageCode: string;
        contactTypeName: string;
      }
    | {
        id: string;
        languageCode: string;
        contactTypeName: string;
        version: number;
      }
  >;
}

export interface U3152ContactTypeDeleteBody {
  id: string;
  version: number;
}

export const U3152_ERROR_CODE_DUPLICATE = 'BU315201';
export const U3152_ERROR_CODE_IN_USE = 'BU315202';

export function createEmptyU3152FormRow(): U3152ContactTypeFormRow {
  return {
    id: '',
    contactTypeCode: '',
    active: true,
    version: 0,
    translations: [
      { languageCode: 'fr', contactTypeName: '' },
      { languageCode: 'en', contactTypeName: '' },
    ],
  };
}

export function normalizeU3152FromApi(row: U3152ContactType): U3152ContactType {
  return {
    ...row,
    active: row.active !== false,
    translations: Array.isArray(row.translations)
      ? row.translations.map((tr) => ({
          ...tr,
          languageCode: typeof tr.languageCode === 'string' ? tr.languageCode.trim().toLowerCase() : tr.languageCode,
        }))
      : [],
  };
}

function u3152TranslationDescription(
  translations: U3152ContactTypeTranslation[] | undefined,
  lang: 'fr' | 'en',
): string | undefined {
  if (!Array.isArray(translations)) return undefined;
  const tr = translations.find((t) => t.languageCode?.trim().toLowerCase() === lang);
  const desc = tr?.contactTypeName?.trim();
  return desc || undefined;
}

/** Reconstruit une ligne search (descriptions FR/EN) à partir du détail fiche — évite la perte d'affichage liste après GET/save. */
export function u3152FormRowToSearchRow(
  row: U3152ContactType | U3152ContactTypeFormRow,
  existing?: U3152ContactTypeSearchRow | null,
): U3152ContactTypeSearchRow {
  const searchRow: U3152ContactTypeSearchRow = {
    id: row.id,
    contactTypeCode: row.contactTypeCode,
    active: row.active !== false,
    contactTypeNameFr: u3152TranslationDescription(row.translations, 'fr'),
    contactTypeNameEn: u3152TranslationDescription(row.translations, 'en'),
    version: row.version,
  };
  if (existing) {
    return { ...existing, ...searchRow };
  }
  return searchRow;
}

export function u3152FormatContactTypeListLabel(row: U3152ContactTypeSearchRow, language: string): string {
  const code = row.contactTypeCode?.trim() ?? '';
  const isFr = language.startsWith('fr');
  const linguistic =
    (isFr ? row.contactTypeNameFr : row.contactTypeNameEn)?.trim() ||
    row.contactTypeNameFr?.trim() ||
    row.contactTypeNameEn?.trim() ||
    '';
  if (code && linguistic) return `${code} — ${linguistic}`;
  return code || linguistic;
}

export function buildCreateBody(data: U3152ContactTypeFormRow): U3152ContactTypeCreateBody {
  return {
    contactTypeCode: data.contactTypeCode.trim(),
    active: data.active !== false,
    translations: data.translations.map((tr) => ({
      languageCode: tr.languageCode.trim().toLowerCase(),
      contactTypeName: tr.contactTypeName.trim(),
    })),
  };
}

export function buildUpdateBody(data: U3152ContactTypeFormRow): U3152ContactTypeUpdateBody {
  return {
    id: data.id,
    active: data.active !== false,
    version: data.version,
    translations: data.translations.map((tr) => {
      const languageCode = tr.languageCode.trim().toLowerCase();
      const contactTypeName = tr.contactTypeName.trim();
      const hasId = Boolean(tr.id && String(tr.id).trim() !== '');
      if (hasId && tr.id) {
        return {
          id: tr.id,
          languageCode,
          contactTypeName,
          version: typeof tr.version === 'number' ? tr.version : 0,
        };
      }
      return { languageCode, contactTypeName };
    }),
  };
}

export function buildDeleteBody(data: U3152ContactTypeFormRow): U3152ContactTypeDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3152ContactTypeCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3152ContactTypeUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3152ContactTypeDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3152_ERROR_CODE_DUPLICATE:
      errorResult.fieldErrors['contactTypeCode'] = t('errContactTypeCodeDuplicate', { ns: nameSpace });
      break;
    case U3152_ERROR_CODE_IN_USE:
      errorResult.genericErrors.push({
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errDeleteInUse', { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
